'use client';
import ReactMarkdownBase, { Options } from 'react-markdown';
import { ReactNode, ComponentType } from 'react';

const ReactMarkdown = ReactMarkdownBase as unknown as ComponentType<Options>;
import strip from 'remark-strip-html';
import remarkGfm from 'remark-gfm';
import { Anchor } from '@repo/ui/src/components/Anchor';
import { classNames } from '@repo/ui/src/utils/classNames';
import Image from '@repo/ui/src/components/Image';
import { appConfig } from '../../utils/appConfig';
import TikTokEmbed from './TikTokEmbed';

// Converts <br> HTML nodes to markdown break nodes before remark-strip-html
// strips all HTML. This lets CMS editors use line breaks in table cells
// without requiring HTML access, while still stripping all other HTML.
function remarkBrToBreak() {
  return (tree: any) => {
    function walk(node: any) {
      if (!node.children) return;
      // Iterate in reverse so splicing doesn't shift unvisited indices
      for (let i = node.children.length - 1; i >= 0; i--) {
        const child = node.children[i];
        if (child.type === 'html' && /^<br\s*\/?>$/i.test(child.value.trim())) {
          node.children.splice(i, 1, { type: 'break' });
        } else {
          walk(child);
        }
      }
    }
    walk(tree);
  };
}

interface Props extends Options {
  bodySize?: 'xLarge' | 'large' | 'medium' | 'small';
  children?: string;
  className?: string;
  components?: any;
  remarkPlugins?: any[];
}

export const Markdown = ({
  children,
  components = {},
  className,
  bodySize = 'medium',
  remarkPlugins = [],
  ...props
}: Props) => {
  const bodyClassName = classNames(
    bodySize === 'xLarge' && 'tra-text-web-body-xl',
    bodySize === 'large' && 'tra-text-web-body-l',
    bodySize === 'medium' && 'tra-text-web-body-m',
    bodySize === 'small' && 'tra-text-web-body-s',
    className,
  );

  return (
    <ReactMarkdown
      remarkPlugins={[remarkBrToBreak, strip, remarkGfm, ...(remarkPlugins || [])]}
      components={{
        a: (elProps: any) => {
          const childText = Array.isArray(elProps.children)
            ? elProps.children[0]
            : typeof elProps.children === 'string'
              ? elProps.children
              : '';
          if (childText === 'tiktok embed') {
            return <TikTokEmbed href={elProps.href} />;
          }
          const isInternal = elProps.href?.startsWith('/') || elProps.href?.startsWith('#');
          return (
            <Anchor
              className={classNames('tra-text-web-link', elProps.className)}
              externalLink={!isInternal ? elProps.href : undefined}
              link={isInternal ? elProps.href : undefined}
              isExternal={!isInternal}
              {...elProps}
            />
          );
        },
        p: ({ children }: { children: ReactNode }) => (
          <p className={classNames('mb-4', bodyClassName)}>{children}</p>
        ),
        h1: ({ children }: { children: ReactNode }) => (
          <h1 className="tra-text-web-heading-1 mb-4">{children}</h1>
        ),
        h2: ({ children }: { children: ReactNode }) => (
          <h2 className="tra-text-web-heading-2 mb-4">{children}</h2>
        ),
        h3: ({ children }: { children: ReactNode }) => (
          <h3 className="tra-text-web-heading-3 mb-4">{children}</h3>
        ),
        h4: ({ children }: { children: ReactNode }) => (
          <h4 className="tra-text-web-heading-4 mb-4">{children}</h4>
        ),
        h5: ({ children }: { children: ReactNode }) => (
          <h5 className="tra-text-web-heading-5 mb-4">{children}</h5>
        ),
        h6: ({ children }: { children: ReactNode }) => (
          <h6 className="tra-text-web-heading-6 mb-4">{children}</h6>
        ),
        ul: ({ children }: { children: ReactNode }) => <ul className="pl-5 my-4">{children}</ul>,
        ol: ({ children, start }: { children: ReactNode; start?: number }) => (
          <ol className="pl-5" start={start}>
            {children}
          </ol>
        ),
        li: ({ children }: { children: ReactNode }) => (
          <li className={classNames('pb-2', bodyClassName)}>{children}</li>
        ),
        table: ({ children }: { children: ReactNode }) => (
          <div className="overflow-x-auto md:my-6 border border-warm-100 bg-white">
            <table className="min-w-full border-collapse">{children}</table>
          </div>
        ),
        thead: ({ children }: { children: ReactNode }) => (
          <thead className={classNames('bg-warm-100', appConfig.isMeater && 'bg-white')}>
            {children}
          </thead>
        ),
        tbody: ({ children }: { children: ReactNode }) => (
          <tbody
            className={classNames(
              'bg-white divide-y divide-gray-100',
              appConfig.isMeater && '[&>tr:nth-child(odd)]:bg-[#eef2f6]',
            )}
          >
            {children}
          </tbody>
        ),
        tr: ({ children }: { children: ReactNode }) => (
          <tr className="group hover:bg-gray-50 transition-all duration-150">{children}</tr>
        ),
        th: ({ children }: { children: ReactNode }) => (
          <th
            className={classNames(
              'px-4 py-3 text-left tra-text-web-body-l font-bold first:pl-6 last:pr-6 md:px-6 md:py-4',
              appConfig.isMeater && 'uppercase',
            )}
          >
            {children}
          </th>
        ),
        td: ({ children }: { children: ReactNode }) => (
          <td className="px-4 py-3 tra-text-web-body-l border-b border-warm-100 first:pl-6 last:pr-6 md:px-6 md:py-4">
            {children}
          </td>
        ),
        hr: ({ children }: { children: ReactNode }) => <hr className="mb-4">{children}</hr>,
        img: (props: any) =>
          props.src ? (
            <div className="relative h-[600px] aspect-4/3">
              <Image src={props.src} layout="fill" alt={props.alt} objectFit="contain" />
            </div>
          ) : null,

        ...components,
      }}
      {...props}
    >
      {children}
    </ReactMarkdown>
  );
};

export default Markdown;
